// "Then choose a venue if the member has several" (spec/13 §1): a clean card per organisation, each
// venue a row that opens its Floor, on the plain ground the sign-in screens use (`VenueDirectory`). The top
// bar's switcher links here as "All venues and organisations". An owner whose organisation has no venue yet is
// shown the way to its Setup (spec/13 §13.1), not told to ask an owner.

import { useTranslation } from '@clavian/i18n/react';
import { Button, EmptyState } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';

import { useMyOrganizations, useMyVenues } from '../api/queries';
import { isOwnerRole } from '../setup/landing';
import { Loading } from './layouts';
import { LoadFailure } from './LoadFailure';
import { Mark } from './Mark';
import { NavIcon } from './nav-icons';
import { VenueDirectory } from './VenueDirectory';

export function ChooseVenuePage() {
  const { t } = useTranslation('console');
  const venues = useMyVenues();
  const organizations = useMyOrganizations();

  const body = () => {
    if (venues.data === undefined) {
      return venues.isError ? (
        <LoadFailure error={venues.error} message={t('chooseVenue.unavailable')} onRetry={() => void venues.refetch()} />
      ) : (
        <Loading />
      );
    }
    if (venues.data.length === 0) {
      const owned = (organizations.data ?? []).filter((organization) => isOwnerRole(organization.role));
      const first = owned[0];
      if (first !== undefined) {
        return (
          <EmptyState
            icon={<NavIcon name="setup" />}
            title={t('chooseVenue.ownerNoneTitle')}
            description={t('chooseVenue.ownerNone', { organisation: first.name })}
            action={
              <Button asChild variant="primary">
                <Link to="/o/$orgSlug/setup" params={{ orgSlug: first.slug }}>
                  {t('chooseVenue.ownerNoneAction')}
                </Link>
              </Button>
            }
          />
        );
      }
      return <EmptyState icon={<NavIcon name="venues" />} title={t('chooseVenue.noneTitle')} description={t('chooseVenue.none')} />;
    }
    return <VenueDirectory organizations={organizations.data ?? []} venues={venues.data} />;
  };

  return (
    <main className="auth" id="console-main">
      <div className="auth__panel auth__panel--wide">
        <div className="auth__brand" aria-hidden="true">
          <Mark size={32} />
          <span className="auth__wordmark">
            Clavian<span className="console-wordmark__stop">.</span>
          </span>
        </div>
        <div className="auth__head auth__head--centred">
          <h1>{t('chooseVenue.title')}</h1>
          <p className="console-muted">{t('chooseVenue.intro')}</p>
        </div>
        {body()}
      </div>
    </main>
  );
}
